import {
  getCategoryRankingPointsTable,
  importClubRankingPoints,
} from '@/api/category-ranking/categoryRankingApi';
import {
  RANKING_IMPORT_MAX_ROWS,
  type CategoryRankingPointsRule,
  type CategoryRankingStage,
  type RankingImportForm,
  type RankingImportResult,
} from '@/api/category-ranking/types';
import { Button } from '@/components/ui/button';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { useEloCategoriesState } from '@/hooks/useEloCategories';
import type { Sport } from '@/types/sports.model';
import { getCategoryRankingStageLabel } from '@/utils/category-ranking-utils';
import { handleGlobalError } from '@/utils/error-handler';
import { toDateInputFormat } from '@/utils/formatters';
import {
  downloadImportTemplate,
  emptyGridRow,
  parseImportCsv,
  toBackendBirthdate,
  type ImportGridRow,
} from '@/utils/ranking-import-csv';
import {
  importConfigSchema,
  importRowSchema,
  type ImportConfigInput,
} from '@/schemas/rankingImportSchema';
import { Download, LoaderCircle, Upload } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { RankingImportRowsGrid } from './RankingImportRowsGrid';
import { RankingImportSummary } from './RankingImportSummary';

type WizardStep = 'config' | 'rows' | 'confirm';

interface RankingImportModalProps {
  clubId: string;
  /** Already filtered to the club's sports by the panel. */
  sports: Sport[];
  /** The panel's current sport filter, preselected in step 1. */
  defaultSportUid?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful (non-dry-run) import so the panel can refetch. */
  onImported: () => void;
}

const resolveDefaultSport = (
  defaultSportUid: string | undefined,
  sports: Sport[],
): string => defaultSportUid ?? sports[0]?.uid ?? '';

/** What a fresh wizard run looks like — shared by the lazy initializer and resetAndClose. */
const buildInitialConfig = (
  defaultSportUid: string | undefined,
  sports: Sport[],
): ImportConfigInput => ({
  sport: resolveDefaultSport(defaultSportUid, sports),
  tournamentName: '',
  earnedAt: toDateInputFormat(new Date()),
});

const tooManyRowsToast = (count: number) =>
  toast({
    title: 'Demasiadas filas',
    description: `Son ${count} filas y el máximo por lote es ${RANKING_IMPORT_MAX_ROWS}. Dividí la importación en varios lotes.`,
    variant: 'destructive',
  });

export const RankingImportModal: React.FC<RankingImportModalProps> = ({
  clubId,
  sports,
  defaultSportUid,
  open,
  onOpenChange,
  onImported,
}) => {
  const [step, setStep] = useState<WizardStep>('config');
  const [config, setConfig] = useState<ImportConfigInput>(() =>
    buildInitialConfig(defaultSportUid, sports),
  );
  const [configErrors, setConfigErrors] = useState<
    Partial<Record<keyof ImportConfigInput, string>>
  >({});
  const [rows, setRows] = useState<ImportGridRow[]>([emptyGridRow()]);
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [csvErrors, setCsvErrors] = useState<string[]>([]);
  const [pendingCsvRows, setPendingCsvRows] = useState<ImportGridRow[] | null>(
    null,
  );
  const [pointsRules, setPointsRules] = useState<
    CategoryRankingPointsRule[] | null
  >(null);
  const [dryRunResult, setDryRunResult] = useState<RankingImportResult | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  // True only during the real (non-dry-run) POST: unlike `submitting`, this one gates closing the
  // dialog, so a dry-run — which persists nothing — never blocks it.
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Bumped on every resetAndClose (close, or a fresh wizard run), so an in-flight dry-run or
  // submit started under a previous session can detect it was superseded and discard its
  // response instead of clobbering the state of whatever the manager started next.
  const importGenerationRef = useRef(0);

  // Gated on `open` like the pointsRules fetch below: the panel mounts this modal for every
  // privileged user, so an ungated hook would duplicate the panel's own categories request on
  // each page view, wizard or no wizard.
  const { categories } = useEloCategoriesState(
    open ? config.sport || undefined : undefined,
  );

  // Same table the "¿Cómo se suman los puntos?" modal shows: the manager picks an instance per
  // row, so they need to see what each one is worth before loading anything.
  useEffect(() => {
    if (!open || pointsRules) return;
    let cancelled = false;
    getCategoryRankingPointsTable()
      .then((data) => {
        if (!cancelled) setPointsRules(data ?? []);
      })
      .catch(() => {
        if (!cancelled) setPointsRules([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, pointsRules]);

  // The panel mounts this modal before its sports finish loading, so the lazy initializer above
  // can run with an empty list. Re-seed on open, without clobbering a sport the user already picked.
  useEffect(() => {
    if (!open) return;
    setConfig((current) =>
      current.sport
        ? current
        : { ...current, sport: resolveDefaultSport(defaultSportUid, sports) },
    );
  }, [open, defaultSportUid, sports]);

  const resetAndClose = () => {
    importGenerationRef.current += 1;
    setStep('config');
    setConfig(buildInitialConfig(defaultSportUid, sports));
    setConfigErrors({});
    setRows([emptyGridRow()]);
    setRowErrors({});
    setCsvErrors([]);
    setPendingCsvRows(null);
    setDryRunResult(null);
    setSubmitting(false);
    setImporting(false);
    onOpenChange(false);
  };

  /** Categories belong to a sport, so switching it invalidates every category already picked. */
  const changeSport = (sport: string) => {
    setConfig((current) => ({ ...current, sport }));
    setRows((current) =>
      current.map((row) => ({ ...row, categoryId: undefined })),
    );
  };

  const goToRows = () => {
    const parsed = importConfigSchema.safeParse(config);
    if (!parsed.success) {
      const errors: Partial<Record<keyof ImportConfigInput, string>> = {};
      parsed.error.issues.forEach((issue) => {
        const field = issue.path[0] as keyof ImportConfigInput;
        if (!errors[field]) errors[field] = issue.message;
      });
      setConfigErrors(errors);
      return;
    }
    setConfigErrors({});
    setStep('rows');
  };

  /** Any manual edit makes the CSV parse errors stale — the banner describes one parse, not the
   *  grid's current state, so it must not linger once the manager has touched a row by hand. */
  const handleRowsChange = useCallback(
    (update: React.SetStateAction<ImportGridRow[]>) => {
      setRows(update);
      setCsvErrors([]);
    },
    [],
  );

  const applyCsvRows = (parsedRows: ImportGridRow[]) => {
    setRows(parsedRows.length > 0 ? parsedRows : [emptyGridRow()]);
    setRowErrors({});
    toast({
      title: 'Archivo cargado',
      description: `Se cargaron ${parsedRows.length} filas del CSV.`,
    });
  };

  const handleFile = async (file: File) => {
    let text: string;
    try {
      text = await file.text();
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo leer el archivo.',
        variant: 'destructive',
      });
      return;
    }
    const { rows: parsedRows, errors } = parseImportCsv(text, categories);
    // Checked here, before touching any state (setRows/setPendingCsvRows included), and not
    // merged with the same check in validateRows: RankingImportRowsGrid is an unvirtualized
    // table that mounts several Radix Selects/Inputs per row, so setting thousands of rows
    // risks freezing the tab before a post-render toast would ever get the chance to fire.
    if (parsedRows.length > RANKING_IMPORT_MAX_ROWS) {
      tooManyRowsToast(parsedRows.length);
      return;
    }
    setCsvErrors(errors);
    if (parsedRows.length === 0) return;
    const gridHasData = rows.some(
      (row) => row.email || row.name || row.categoryId || row.stage,
    );
    if (gridHasData) {
      setPendingCsvRows(parsedRows);
      return;
    }
    applyCsvRows(parsedRows);
  };

  const validateRows = (): boolean => {
    if (rows.length > RANKING_IMPORT_MAX_ROWS) {
      tooManyRowsToast(rows.length);
      return false;
    }
    const errors: Record<number, string> = {};
    rows.forEach((row, index) => {
      const parsed = importRowSchema.safeParse({
        email: row.email,
        name: row.name || undefined,
        gender: row.gender || undefined,
        birthdate: row.birthdate || undefined,
        categoryId: row.categoryId,
        stage: row.stage,
      });
      if (!parsed.success) errors[index] = parsed.error.issues[0].message;
    });
    setRowErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /** The payload for both the dry-run and the real import. */
  const buildForm = (): RankingImportForm => ({
    sport: config.sport,
    tournamentName: config.tournamentName.trim(),
    earnedAt: config.earnedAt,
    rows: rows.map((row) => {
      const name = row.name.trim();
      return {
        email: row.email.trim(),
        categoryId: row.categoryId as number,
        stage: row.stage as CategoryRankingStage,
        // Only sent for emails without an account; the backend ignores it otherwise.
        newUser: name
          ? {
              name,
              gender: row.gender || undefined,
              birthdate: toBackendBirthdate(row.birthdate),
            }
          : undefined,
      };
    }),
  });

  const runDryRun = async () => {
    const generation = importGenerationRef.current;
    setDryRunResult(null);
    setSubmitting(true);
    try {
      const result = await importClubRankingPoints(clubId, buildForm(), true);
      if (generation !== importGenerationRef.current) return;
      setDryRunResult(result);
    } catch (error) {
      if (generation !== importGenerationRef.current) return;
      handleGlobalError(error, 'Error', 'No se pudo validar la importación.');
      setStep('rows');
    } finally {
      if (generation === importGenerationRef.current) setSubmitting(false);
    }
  };

  /** Sends the dry-run's per-row errors back to the grid so the manager fixes them in place. */
  const backToFix = (result: RankingImportResult) => {
    const errors: Record<number, string> = {};
    result.rows.forEach((row) => {
      if (row.error) errors[row.index] = row.error;
    });
    setRowErrors(errors);
    setDryRunResult(null);
    setStep('rows');
  };

  const submitImport = async () => {
    const generation = importGenerationRef.current;
    setSubmitting(true);
    setImporting(true);
    try {
      const result = await importClubRankingPoints(clubId, buildForm(), false);
      if (generation !== importGenerationRef.current) return;
      // batchId null here means a row went bad between the dry-run and the confirm (e.g. someone
      // registered that email in between): nothing was persisted, so it's a dry-run with errors.
      if (!result.batchId) {
        setDryRunResult(result);
        toast({
          title: 'No se importó nada',
          description:
            'Aparecieron errores en algunas filas. Revisalas y volvé a intentar.',
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'Importación completada',
        description: `Se importaron ${result.validRows} filas (${result.usersToCreate.length} cuentas nuevas).`,
      });
      onImported();
      resetAndClose();
    } catch (error) {
      if (generation !== importGenerationRef.current) return;
      handleGlobalError(error, 'Error', 'No se pudo completar la importación.');
    } finally {
      if (generation === importGenerationRef.current) {
        setSubmitting(false);
        setImporting(false);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Only the real import (mutates data) blocks closing. The dry-run persists nothing, the
        // generation ref already makes a late dry-run response inert, and the shared axios
        // instance has no timeout — if it hangs, the manager needs a way out other than reloading.
        if (next || importing) return;
        resetAndClose();
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar puntos de ranking</DialogTitle>
          <DialogDescription>
            Cargá el ranking que el club venía llevando por fuera de GetMatch.
            Los puntos suman al ranking del club, no al ranking general.
          </DialogDescription>
        </DialogHeader>

        {step === 'config' && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="import-sport">Deporte</Label>
              <Select value={config.sport} onValueChange={changeSport}>
                <SelectTrigger id="import-sport" className="mt-1 w-[220px]">
                  <SelectValue placeholder="Deporte" />
                </SelectTrigger>
                <SelectContent>
                  {sports.map((sport) => (
                    <SelectItem key={sport.uid} value={sport.uid}>
                      {sport.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {configErrors.sport && (
                <p className="mt-1 text-xs text-red-600">{configErrors.sport}</p>
              )}
            </div>

            <div>
              <Label htmlFor="import-tournament-name">Nombre del torneo</Label>
              <Input
                id="import-tournament-name"
                className="mt-1"
                placeholder="Ej: Torneo Apertura 2025"
                value={config.tournamentName}
                onChange={(event) =>
                  setConfig((current) => ({
                    ...current,
                    tournamentName: event.target.value,
                  }))
                }
              />
              <p className="mt-1 text-xs text-gray-500">
                Es el nombre que los jugadores van a ver en el detalle de sus
                puntos. Podés importar un lote por torneo o todo junto como
                &ldquo;Ranking histórico 2025&rdquo;.
              </p>
              {configErrors.tournamentName && (
                <p className="mt-1 text-xs text-red-600">
                  {configErrors.tournamentName}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="import-earned-at">Fecha de los puntos</Label>
              <Input
                id="import-earned-at"
                type="date"
                className="mt-1 w-[220px]"
                value={config.earnedAt}
                onChange={(event) =>
                  setConfig((current) => ({
                    ...current,
                    earnedAt: event.target.value,
                  }))
                }
              />
              <p className="mt-1 text-xs text-gray-500">
                Define en qué período del ranking cuentan estos puntos (este
                mes, este año, histórico).
              </p>
              {configErrors.earnedAt && (
                <p className="mt-1 text-xs text-red-600">
                  {configErrors.earnedAt}
                </p>
              )}
            </div>

            {pointsRules && pointsRules.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="mb-2 text-xs font-medium text-gray-600">
                  Puntos por instancia
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                  {pointsRules.map((rule) => (
                    <span key={rule.stage}>
                      {getCategoryRankingStageLabel(rule.stage)}:{' '}
                      <strong>{rule.points}</strong>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'rows' && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => downloadImportTemplate(categories)}
              >
                <Download size={16} className="mr-1.5" />
                Descargar template
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={16} className="mr-1.5" />
                Subir CSV
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  // Cleared so re-picking the same file fires change again.
                  event.target.value = '';
                  if (file) void handleFile(file);
                }}
              />
            </div>

            {csvErrors.length > 0 && (
              <ul className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                {csvErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            )}

            <RankingImportRowsGrid
              rows={rows}
              onChange={handleRowsChange}
              categories={categories}
              rowErrors={rowErrors}
            />
          </div>
        )}

        {step === 'confirm' &&
          (dryRunResult === null ? (
            <div className="flex justify-center py-12">
              <LoaderCircle className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <RankingImportSummary
              result={dryRunResult}
              tournamentName={config.tournamentName.trim()}
              earnedAt={config.earnedAt}
            />
          ))}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            disabled={importing}
            onClick={resetAndClose}
          >
            Cancelar
          </Button>
          <div className="flex gap-2">
            {step !== 'config' && (
              <Button
                type="button"
                variant="outline"
                disabled={submitting}
                onClick={() => {
                  // Leaving confirm invalidates the dry-run shown there; backToFix already does
                  // the same before sending the manager back to fix rows.
                  if (step === 'confirm') setDryRunResult(null);
                  // Same reasoning as handleRowsChange: once the manager has navigated away,
                  // any CSV parse errors still shown would be stale by the time they come back.
                  setCsvErrors([]);
                  setStep(step === 'confirm' ? 'rows' : 'config');
                }}
              >
                Atrás
              </Button>
            )}
            {step === 'config' && (
              <Button type="button" onClick={goToRows}>
                Siguiente
              </Button>
            )}
            {step === 'rows' && (
              <Button
                type="button"
                onClick={() => {
                  if (!validateRows()) return;
                  setStep('confirm');
                  void runDryRun();
                }}
              >
                Siguiente
              </Button>
            )}
            {step === 'confirm' &&
              dryRunResult !== null &&
              (dryRunResult.rows.some((row) => row.error) ? (
                <Button type="button" onClick={() => backToFix(dryRunResult)}>
                  Volver y corregir
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={submitting}
                  onClick={() => void submitImport()}
                >
                  {submitting && (
                    <LoaderCircle size={16} className="mr-1.5 animate-spin" />
                  )}
                  Importar {dryRunResult.validRows} filas
                </Button>
              ))}
          </div>
        </DialogFooter>
      </DialogContent>

      <ConfirmationModal
        isOpen={pendingCsvRows !== null}
        onClose={() => {
          setPendingCsvRows(null);
          setCsvErrors([]);
        }}
        onConfirm={() => {
          if (pendingCsvRows) applyCsvRows(pendingCsvRows);
          setPendingCsvRows(null);
        }}
        title="Reemplazar las filas cargadas"
        description="El archivo va a reemplazar las filas que ya tenés en la grilla. ¿Seguimos?"
        confirmText="Reemplazar"
      />
    </Dialog>
  );
};
